import { Alert, Stack, Typography } from '@mui/material';
import React from 'react';
import AwsUploadCommandGenerator from './AwsUploadCommandGenerator';
import { CopyableBlock } from './CopyableCliBlock';

const S3_URI_PATTERN = /^s3:\/\/[^/\s]+(?:\/.*)?$/;

const isValidS3Uri = (value: string) => S3_URI_PATTERN.test(value);

const GrantBasedAwsUploadCommandGenerator = ({
  awsProfileName,
  slideDirectory,
}: {
  awsProfileName: string;
  slideDirectory: string;
}) => {
  return (
    <Stack spacing={1.5}>
      {!slideDirectory ? (
        <>
          <Typography variant="body2" color="text.secondary">
            Enter the source S3 file or folder, then generate a complete AWS CLI command for this study.
          </Typography>
          <Alert severity="error">
            Upload destination could not be loaded from the study. Please contact Nucleai support.
          </Alert>
        </>
      ) : (
        <AwsUploadCommandGenerator
          copyableCommandBlock={(command) => (
            <CopyableBlock text={command} copyButtonAriaLabel="Copy AWS CLI upload command" />
          )}
          destination={slideDirectory}
          footerContent={
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary">
                Folder mode includes nested files and subfolders. File mode copies only the single S3 object you enter.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Your AWS credentials must have read access to the source bucket or prefix. Unsupported file extensions
                may later appear as ingest warnings instead of processing successfully.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                If the study upload folder already contains the same destination key, AWS CLI writes to that same path.
                You can rerun the command after a failed transfer, but review the destination path carefully before
                retrying.
              </Typography>
            </Stack>
          }
          intro="Enter the source file or folder path from your S3 storage, where your slides are currently kept."
          invalidSourceText="Use an S3 path in the form s3://bucket-name/optional/path-to-file-or-folder."
          isSourceValid={isValidS3Uri}
          sourceLabels={{ recursive: 'Source folder or S3 URI', singleFile: 'Source file or S3 URI' }}
          uploadOptions={[
            ...(awsProfileName ? [`--profile ${awsProfileName}`] : []),
            '--acl bucket-owner-full-control',
            '--sse AES256',
          ]}
        />
      )}
    </Stack>
  );
};

export default GrantBasedAwsUploadCommandGenerator;
